import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { reGenerateDefine, getExcelView, getSaveUpdateDelete } from "../../../api/api";
import styles from './ExcelViewer.module.css';
import Modal from '../Modal.tsx';
import { useToast } from '../../toast/ToastProvider.tsx';

interface SheetData {
  sheetName: string;
  rowCount: number;
  columns: string[];
  records: Record<string, any>[];
}

interface ExcelData {
  fileName: string;
  generatedAt: string;
  totalSheets: number;
  totalRecords: number;
  sheets: SheetData[];
}

interface ExcelViewerProps {
  revisionNumber?: number;
  onClose?: () => void;
  onSave?: (data: ExcelData) => void;
}

interface SortConfig {
  column: string;
  direction: 'asc' | 'desc';
}

interface FilterConfig {
  [column: string]: string[];
}

const ExcelViewer: React.FC<ExcelViewerProps> = ({ revisionNumber: propRevisionNumber, onClose, onSave }) => {
  const { revisionNumber: urlRevisionNumber } = useParams<{ revisionNumber: string }>();
  const [searchParams] = useSearchParams();
  const studyId = searchParams.get('studyId');
  const studyName = searchParams.get('studyName')
  const navigate = useNavigate();
  const showToast = useToast();
  const revisionNumber = propRevisionNumber || parseInt(urlRevisionNumber || '0');
  const [excelData, setExcelData] = useState<ExcelData | null>(null);
  const [originalData, setOriginalData] = useState<ExcelData | null>(null);
  const [deletedIds, setDeletedIds] = useState<string[]>([]);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [editingCell, setEditingCell] = useState<{ rowIndex: number; colKey: string } | null>(null);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

  // ── Re-arrange mode ──────────────────────────────────────────────────────────
  // isRearrangeMode: drag/drop is active; editing & delete are disabled
  // rearrangeBaseData: snapshot taken when entering re-arrange mode so Cancel can revert
  const [isRearrangeMode, setIsRearrangeMode] = useState(false);
  const [rearrangeBaseData, setRearrangeBaseData] = useState<ExcelData | null>(null);
  const [savingRearrange, setSavingRearrange] = useState(false);

  // Search, Sort, Filter states
  const [globalSearch, setGlobalSearch] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [filters, setFilters] = useState<FilterConfig>({});
  const [showFilterDropdown, setShowFilterDropdown] = useState<string | null>(null);
  const [filterSearch, setFilterSearch] = useState('');

  // Drag and drop states
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [draggedRows, setDraggedRows] = useState<number[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [targetRowNumber, setTargetRowNumber] = useState<string>('');
  const [dragPreviewPosition, setDragPreviewPosition] = useState<{ x: number; y: number } | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<number | null>(null);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [isDropZoneHovered, setIsDropZoneHovered] = useState(false);
  const [rowsToMove, setRowsToMove] = useState<number[]>([]);

  // Column resize states
  const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>({});
  // const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchExcelData();
  }, [revisionNumber]);

  // Load saved row order from localStorage on component mount
  useEffect(() => {
    if (excelData) {
      const savedOrder = localStorage.getItem(`excel-row-order-${revisionNumber}-${activeSheetIndex}`);
      if (savedOrder) {
        try {
          const orderData = JSON.parse(savedOrder);
          applySavedRowOrder(orderData);
        } catch (error) {
          console.error('Error loading saved row order:', error);
        }
      }
    }
  }, [excelData?.fileName, activeSheetIndex]); // only run when data first loads, not on every update

  const applySavedRowOrder = (orderData: any[]) => {
    if (!excelData) return;

    const updatedData = { ...excelData };
    const sheet = { ...updatedData.sheets[activeSheetIndex] };
    updatedData.sheets = [...updatedData.sheets];
    updatedData.sheets[activeSheetIndex] = sheet;

    if (sheet && orderData.length > 0) {
      const reorderedRecords = orderData
        .map((item: any) => sheet.records.find(record => record._id === item._id))
        .filter((record): record is Record<string, any> => record !== undefined);

      if (reorderedRecords.length === sheet.records.length) {
        sheet.records = reorderedRecords;
        setExcelData(updatedData);
        setOriginalData(JSON.parse(JSON.stringify(updatedData)));
      }
    }
  };

  const saveRowOrderToLocalStorage = (data: ExcelData) => {
    const sheet = data.sheets[activeSheetIndex];
    const orderData = sheet.records.map(record => ({
      _id: record._id,
      _excelRow: record._excelRow,
    }));
    localStorage.setItem(`excel-row-order-${revisionNumber}-${activeSheetIndex}`, JSON.stringify(orderData));
  };

  // Column resize handler
  const handleColumnResize = (column: string, startX: number) => {
    // setResizingColumn(column);
    const startWidth = columnWidths[column] || 150;

    const handleMouseMove = (e: MouseEvent) => {
      const diff = e.clientX - startX;
      const newWidth = Math.max(100, Math.min(500, startWidth + diff));
      setColumnWidths(prev => ({ ...prev, [column]: newWidth }));
    };

    const handleMouseUp = () => {
      // setResizingColumn(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target as Node)
      ) {
        setShowFilterDropdown(null);
        setFilterSearch('');
      }
    };

    if (showFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showFilterDropdown]);

  // Handle drag preview position
  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e: MouseEvent) => {
        setDragPreviewPosition({ x: e.clientX, y: e.clientY });
      };
      document.addEventListener('mousemove', handleMouseMove);
      return () => document.removeEventListener('mousemove', handleMouseMove);
    } else {
      setDragPreviewPosition(null);
    }
  }, [isDragging]);

  const fetchExcelData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getExcelView(revisionNumber);
      const data: ExcelData = response.data;

      // Sort each sheet's records by _excelRow to guarantee display order
      data.sheets.forEach(sheet => {
        sheet.records.sort((a, b) => (a._excelRow ?? 0) - (b._excelRow ?? 0));
      });

      setExcelData(data);
      setOriginalData(JSON.parse(JSON.stringify(data)));
    } catch (err: any) {
      console.error('Error fetching excel data:', err);
      setError(err.response?.data?.message || 'Failed to load Excel data');
    } finally {
      setLoading(false);
    }
  };

  const proceedWithGenerateDefine = async () => {
    const response = await reGenerateDefine(studyId);
    if (response.status === 200) {
      showToast.success(response.data.message);
      navigate(`/defineui/assigned-studies`, { replace: true });
    }
  };

  const handleConfirmGenerateDefine = () => {
    setShowUnsavedChangesModal(false);
    proceedWithGenerateDefine();
  };

  const handleCancelGenerateDefine = () => {
    setShowUnsavedChangesModal(false);
  };

  const handleConfirmDelete = () => {
    if (rowToDelete !== null) {
      proceedWithDelete(rowToDelete);
    }
    setShowDeleteModal(false);
    setRowToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setRowToDelete(null);
  };

  const proceedWithDelete = (rowIndex: number) => {
    if (!excelData) return;

    const updatedData = { ...excelData };
    const sheet = { ...updatedData.sheets[activeSheetIndex] };
    updatedData.sheets = [...updatedData.sheets];
    updatedData.sheets[activeSheetIndex] = sheet;

    const recordToDelete = filteredAndSortedData[rowIndex];

    if (recordToDelete._id) {
      setDeletedIds(prev => [...prev, recordToDelete._id]);
    }

    const actualIndex = sheet.records.findIndex(r => r._id === recordToDelete._id);
    if (actualIndex !== -1) {
      sheet.records = [...sheet.records];
      sheet.records.splice(actualIndex, 1);
      sheet.rowCount = sheet.records.length;
      updatedData.totalRecords -= 1;

      sheet.records.forEach((record, idx) => {
        record._excelRow = idx + 1;
      });

      setExcelData(updatedData);
      setHasChanges(true);
    }
  };

  const handleConfirmClose = () => {
    setShowCloseModal(false);
    if (onClose) {
      onClose();
    } else {
      navigate('/defineui/assigned-studies', { replace: true });
    }
  };

  const handleCancelClose = () => {
    setShowCloseModal(false);
  };

  // ── Re-arrange mode handlers ─────────────────────────────────────────────────

  const handleEnterRearrangeMode = () => {
    // Snapshot current data so we can revert on Cancel
    setRearrangeBaseData(JSON.parse(JSON.stringify(excelData)));
    setEditingCell(null);
    setSelectedRows(new Set());
    setIsRearrangeMode(true);
  };

  const handleCancelRearrange = () => {
    // Revert to the snapshot taken when entering re-arrange mode
    if (rearrangeBaseData) {
      setExcelData(JSON.parse(JSON.stringify(rearrangeBaseData)));
    }
    setRearrangeBaseData(null);
    setIsRearrangeMode(false);
    setSelectedRows(new Set());
    setDraggedRows([]);
    setIsDragging(false);
    setDragOverIndex(null);
  };

  /**
   * Save rearrange: build payload containing ONLY rows whose _excelRow changed
   * relative to the snapshot taken when entering rearrange mode.
   * Format: { _id, fields: [{ _excelRow: <newValue> }] }
   */
  const handleSaveRearrange = async () => {
    if (!excelData || !rearrangeBaseData || savingRearrange) return;

    try {
      setSavingRearrange(true);

      const activeSheet = excelData.sheets[activeSheetIndex];
      const baseSheet = rearrangeBaseData.sheets[activeSheetIndex];

      // Map original _excelRow values by _id for quick lookup
      const baseIndexById: Record<string, number> = {};
      baseSheet.records.forEach((record, idx) => {
        if (record._id) {
          baseIndexById[record._id] = idx;
        }
      });

      // Build reshuffle records: every row whose _excelRow now differs from base
      const reshuffleRecords: any[] = [];
      activeSheet.records.forEach((record, idx) => {
        if (!record._id) return;
        const baseIdx = baseIndexById[record._id];
        if (baseIdx !== undefined && baseIdx !== idx) {
          reshuffleRecords.push({
            _id: record._id,
            fields: [{ _excelRow: idx + 1 }], // 1-based, no header offset
          });
        }
      });

      if (reshuffleRecords.length === 0) {
        showToast.success('No row order changes to save');
        setRearrangeBaseData(null);
        setIsRearrangeMode(false);
        return;
      }

      const payload = {
        revisionId: revisionNumber,
        sheetName: activeSheet.sheetName,
        userId: 'test.user',
        records: reshuffleRecords,
        deleteIds: [],
      };

      console.log('Rearrange payload:', JSON.stringify(payload, null, 2));

      const response = await getSaveUpdateDelete(payload);
      console.log('Server response:', response);

      // Update _excelRow on all records to reflect new positions
      // const updatedData: ExcelData = JSON.parse(JSON.stringify(excelData));
      // updatedData.sheets[activeSheetIndex].records.forEach((record, idx) => {
      //   record._excelRow = idx + 2;
      // });

      // setExcelData(updatedData);
      // setOriginalData(JSON.parse(JSON.stringify(updatedData)));
      // saveRowOrderToLocalStorage(updatedData);
      await fetchExcelData();
      setRearrangeBaseData(null);
      setIsRearrangeMode(false);
      setSelectedRows(new Set());

      showToast.success('Row order saved successfully');

      if (onSave) {
        onSave(excelData);
      }
    } catch (error: any) {
      console.error('Error saving rearrange:', error);
      showToast.error('Failed to save row order: ' + (error.response?.data?.message || error.message));
    } finally {
      setSavingRearrange(false);
    }
  };

  // ── Get unique values for a column (for filtering) ───────────────────────────

  const getUniqueColumnValues = (columnKey: string): string[] => {
    if (!excelData) return [];

    const sheet = excelData.sheets[activeSheetIndex];
    const uniqueValues = new Set<string>();
    let hasEmptyValues = false;

    sheet.records.forEach(record => {
      const value = record[columnKey];
      if (value !== null && value !== undefined && value !== '') {
        uniqueValues.add(String(value));
      } else {
        hasEmptyValues = true;
      }
    });

    const result = Array.from(uniqueValues).sort();
    if (hasEmptyValues) result.unshift('(empty)');

    return result;
  };

  const getFilteredOptions = (columnKey: string): string[] => {
    const allValues = getUniqueColumnValues(columnKey);
    if (!filterSearch) return allValues;
    return allValues.filter(value => value.toLowerCase().includes(filterSearch.toLowerCase()));
  };

  const handleGenerateDefine = async () => {
    if (hasChanges) {
      setShowUnsavedChangesModal(true);
      return;
    }
    proceedWithGenerateDefine();
  };

  // ── Apply search, filter, and sort ───────────────────────────────────────────

  const filteredAndSortedData = useMemo(() => {
    if (!excelData) return [];

    const sheet = excelData.sheets[activeSheetIndex];
    let processedData = [...sheet.records];

    if (globalSearch) {
      const searchLower = globalSearch.toLowerCase();
      processedData = processedData.filter(record =>
        Object.values(record).some(value => String(value).toLowerCase().includes(searchLower))
      );
    }

    Object.keys(filters).forEach(column => {
      if (filters[column].length > 0) {
        processedData = processedData.filter(record => {
          const value = record[column];
          const stringValue = String(value ?? '');
          return filters[column].some(filterValue => {
            if (filterValue === '(empty)') {
              return value === null || value === undefined || value === '';
            }
            return stringValue === filterValue;
          });
        });
      }
    });

    if (sortConfig) {
      processedData.sort((a, b) => {
        const aValue = a[sortConfig.column];
        const bValue = b[sortConfig.column];

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        const aNum = Number(aValue);
        const bNum = Number(bValue);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
        }

        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();
        if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      // Default sort by _excelRow to maintain original Excel order
      processedData.sort((a, b) => {
        const aRow = a._excelRow || 0;
        const bRow = b._excelRow || 0;
        return aRow - bRow;
      });
    }

    return processedData;
  }, [excelData, activeSheetIndex, globalSearch, filters, sortConfig]);

  const handleSort = (columnKey: string) => {
    setSortConfig(current => {
      if (!current || current.column !== columnKey) return { column: columnKey, direction: 'asc' };
      if (current.direction === 'asc') return { column: columnKey, direction: 'desc' };
      return null;
    });
  };

  const handleFilterToggle = (columnKey: string, value: string) => {
    setFilters(current => {
      const currentFilters = current[columnKey] || [];
      const newFilters = { ...current };
      if (currentFilters.includes(value)) {
        newFilters[columnKey] = currentFilters.filter(v => v !== value);
      } else {
        newFilters[columnKey] = [...currentFilters, value];
      }
      if (newFilters[columnKey].length === 0) delete newFilters[columnKey];
      return newFilters;
    });
  };

  const clearFilters = () => {
    setFilters({});
    setGlobalSearch('');
    setSortConfig(null);
  };

  const resetChanges = () => {
    setDeletedIds([]);
    setHasChanges(false);
  };

  const handleCellEdit = (rowIndex: number, columnKey: string, newValue: any) => {
    if (!excelData) return;

    const updatedData = { ...excelData };
    const sheet = { ...updatedData.sheets[activeSheetIndex] };
    updatedData.sheets = [...updatedData.sheets];
    updatedData.sheets[activeSheetIndex] = sheet;
    sheet.records = [...sheet.records];

    const actualRecordIndex = sheet.records.findIndex(r => r === filteredAndSortedData[rowIndex]);
    if (actualRecordIndex !== -1) {
      sheet.records[actualRecordIndex] = { ...sheet.records[actualRecordIndex], [columnKey]: newValue };
      setExcelData(updatedData);
      setHasChanges(true);
    }
  };

  const handleAddRow = () => {
    if (!excelData) return;

    const updatedData = { ...excelData };
    const sheet = { ...updatedData.sheets[activeSheetIndex] };
    updatedData.sheets = [...updatedData.sheets];
    updatedData.sheets[activeSheetIndex] = sheet;
    sheet.records = [...sheet.records];

    const newRow: Record<string, any> = {
      _id: null,
      _excelRow: sheet.records.length + 1,
    };

    sheet.columns.forEach(col => {
      if (col !== '_id' && col !== '_excelRow') newRow[col] = '';
    });

    sheet.records.push(newRow);
    sheet.rowCount = sheet.records.length;
    updatedData.totalRecords += 1;

    setExcelData(updatedData);
    setHasChanges(true);

    setTimeout(() => {
      if (tableRef.current) tableRef.current.scrollTop = tableRef.current.scrollHeight;
    }, 100);
  };

  const handleDeleteRow = (rowIndex: number) => {
    setRowToDelete(rowIndex);
    setShowDeleteModal(true);
  };

  // ── Main save (edit/add/delete changes, NOT rearrange) ───────────────────────

  const handleSave = async () => {
    if (!excelData || !originalData || saving) return;

    try {
      setSaving(true);
      const activeSheet = excelData.sheets[activeSheetIndex];
      const originalSheet = originalData.sheets[activeSheetIndex];

      const records: any[] = [];

      activeSheet.records.forEach(currentRecord => {
        if (currentRecord._id === null) {
          // New row
          const fields: any[] = [];
          activeSheet.columns.forEach(col => {
            if (col !== '_id' && col !== '_excelRow' && currentRecord[col] !== '') {
              const field: any = {};
              field[col] = currentRecord[col];
              fields.push(field);
            }
          });
          if (fields.length > 0) {
            records.push({ _id: null, fields });
          }
        } else {
          // Existing row — only include changed fields
          const originalRecord = originalSheet.records.find(r => r._id === currentRecord._id);
          if (originalRecord) {
            const fields: any[] = [];
            let changed = false;
            activeSheet.columns.forEach(col => {
              if (col !== '_id' && col !== '_excelRow') {
                if (currentRecord[col] !== originalRecord[col]) {
                  const field: any = {};
                  field[col] = currentRecord[col];
                  fields.push(field);
                  changed = true;
                }
              }
            });
            if (changed) {
              records.push({ _id: currentRecord._id, fields });
            }
          }
        }
      });

      const payload = {
        revisionId: revisionNumber,
        sheetName: activeSheet.sheetName,
        userId: 'test.user',
        records,
        deleteIds: deletedIds,
      };

      console.log('Save payload:', JSON.stringify(payload, null, 2));

      const response = await getSaveUpdateDelete(payload);
      console.log('Server response:', response);

      await fetchExcelData();
      showToast.success('Changes saved successfully');

      setDeletedIds([]);
      setHasChanges(false);

      if (onSave) onSave(excelData);
    } catch (error: any) {
      console.error('Error saving data:', error);
      showToast.error('Failed to save data: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      setShowCloseModal(true);
    } else {
      if (onClose) {
        onClose();
      } else {
        navigate('/defineui/assigned-studies');
      }
    }
  };

  const handleCellClick = (rowIndex: number, colKey: string) => {
    if (isRearrangeMode) return; // disable editing in rearrange mode
    setEditingCell({ rowIndex, colKey });
  };

  const handleCellBlur = () => {
    setEditingCell(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent, rowIndex: number, colKey: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setEditingCell(null);
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  };

  // ── Drag and drop handlers ───────────────────────────────────────────────────

  const handleRowSelect = (rowIndex: number) => {
    if (!isRearrangeMode) return;
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowIndex)) {
        newSet.delete(rowIndex);
      } else {
        newSet.add(rowIndex);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (!isRearrangeMode) return;
    if (selectedRows.size === filteredAndSortedData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(filteredAndSortedData.map((_, index) => index)));
    }
  };

  const handleDragStart = (e: React.DragEvent, rowIndex: number) => {
    if (!isRearrangeMode) {
      e.preventDefault();
      return;
    }
    const rowsToDrag = selectedRows.has(rowIndex)
      ? Array.from(selectedRows).sort((a, b) => a - b)
      : [rowIndex];

    setDraggedRows(rowsToDrag);
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';

    const dragImage = document.createElement('div');
    dragImage.style.opacity = '0';
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);
    setTimeout(() => document.body.removeChild(dragImage), 0);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setDraggedRows([]);
    setDragOverIndex(null);
    setDragPreviewPosition(null);
    setIsDropZoneHovered(false);
  };

  const handleDragOver = (e: React.DragEvent, rowIndex: number) => {
    if (!isRearrangeMode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(rowIndex);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  /**
   * Core reorder logic: removes dragged rows from the array and re-inserts them
   * at the drop position. Also reassigns _excelRow = idx + 2 for every record.
   */
  const reorderRows = (
    currentData: ExcelData,
    rowsToReorder: number[],
    dropIndex: number,
    isDroppingDown: boolean,
  ): ExcelData => {
    const updatedData: ExcelData = JSON.parse(JSON.stringify(currentData));
    const sheet = updatedData.sheets[activeSheetIndex];

    const recordsToMove = rowsToReorder.map(rowIndex => filteredAndSortedData[rowIndex]);
    const remainingRecords = sheet.records.filter(
      record => !recordsToMove.some(draggedRecord => draggedRecord._id === record._id)
    );

    const dropRecord = filteredAndSortedData[dropIndex];
    const insertIndex = remainingRecords.findIndex(record => record._id === dropRecord._id);

    if (isDroppingDown) {
      remainingRecords.splice(insertIndex + 1, 0, ...recordsToMove);
    } else {
      remainingRecords.splice(insertIndex, 0, ...recordsToMove);
    }

    sheet.records = remainingRecords;
    // Always recalculate _excelRow after reorder
    sheet.records.forEach((record, idx) => {
      record._excelRow = idx + 1;
    });

    return updatedData;
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (!isRearrangeMode) return;
    setDragOverIndex(null);

    if (draggedRows.length === 0 || !excelData) return;
    if (draggedRows.includes(dropIndex)) return;

    const isDraggingDown = draggedRows[draggedRows.length - 1] < dropIndex;
    const updatedData = reorderRows(excelData, draggedRows, dropIndex, isDraggingDown);

    setExcelData(updatedData);
    setSelectedRows(new Set());
    showToast.success(`Moved ${draggedRows.length} row${draggedRows.length > 1 ? 's' : ''}`);
  };

  const moveRowsToPosition = (targetPosition: number, rowsToReorder: number[]): boolean => {
    if (rowsToReorder.length === 0 || !excelData) return false;

    if (targetPosition < 1 || targetPosition > filteredAndSortedData.length) {
      showToast.error(`Invalid row number. Please enter a number between 1 and ${filteredAndSortedData.length}`);
      return false;
    }

    const updatedData: ExcelData = JSON.parse(JSON.stringify(excelData));
    const sheet = updatedData.sheets[activeSheetIndex];

    const recordsToMove = rowsToReorder.map(rowIndex => filteredAndSortedData[rowIndex]);
    const remainingRecords = sheet.records.filter(
      record => !recordsToMove.some(draggedRecord => draggedRecord._id === record._id)
    );

    const insertIndex = targetPosition - 1;
    remainingRecords.splice(insertIndex, 0, ...recordsToMove);

    sheet.records = remainingRecords;
    sheet.records.forEach((record, idx) => {
      record._excelRow = idx + 1;
    });

    setExcelData(updatedData);
    setSelectedRows(new Set());
    showToast.success(`Moved ${rowsToReorder.length} row${rowsToReorder.length > 1 ? 's' : ''} to position ${targetPosition}`);
    return true;
  };

  // Drop zone handlers
  const handleDropZoneDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setIsDropZoneHovered(true);
  };

  const handleDropZoneDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDropZoneHovered(false);
  };

  const handleDropZoneDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDropZoneHovered(false);
    if (draggedRows.length > 0) {
      setRowsToMove([...draggedRows]);
      setShowMoveModal(true);
    }
  };

  const handleDropZoneClick = () => {
    if (draggedRows.length > 0) {
      setRowsToMove([...draggedRows]);
      setShowMoveModal(true);
    }
  };

  const handleMoveToRow = () => {
    const targetRow = parseInt(targetRowNumber);
    if (isNaN(targetRow)) {
      showToast.error('Please enter a valid row number');
      return;
    }
    const success = moveRowsToPosition(targetRow, rowsToMove);
    if (success) {
      setShowMoveModal(false);
      setTargetRowNumber('');
      setRowsToMove([]);
      setDraggedRows([]);
      setIsDragging(false);
    }
  };

  const handleCancelMove = () => {
    setShowMoveModal(false);
    setTargetRowNumber('');
    setRowsToMove([]);
    setDraggedRows([]);
    setIsDragging(false);
    setDragPreviewPosition(null);
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className={styles.loaderContainer}>
        <div className={styles.spinner}></div>
        <p>Loading Excel data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className={styles.errorIcon}>
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
          <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <h3>Error Loading Excel Data</h3>
        <p>{error}</p>
        <div className={styles.errorButtons}>
          <button onClick={fetchExcelData} className={styles.retryButton}>Retry</button>
          <button onClick={() => navigate(-1)} className={styles.backButton}>Back</button>
        </div>
      </div>
    );
  }

  if (!excelData) return null;

  const activeSheet = excelData.sheets[activeSheetIndex];
  const visibleColumns = activeSheet.columns.filter(col => col !== '_id');
  const activeFilterCount = Object.keys(filters).length;
  const hasActiveFilters = activeFilterCount > 0 || globalSearch || sortConfig;

  return (
    <div className={styles.excelViewerContainer}>

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h2 className={styles.fileName}>{excelData.fileName}</h2>
          {studyName && (
            <span className={styles.studyName}>
              Study: {studyName}
            </span>
          )}
          <span className={styles.fileInfo}>
            Generated: {new Date(excelData.generatedAt).toLocaleString()}
          </span>
          <span className={styles.fileInfo}>
            Showing: {filteredAndSortedData.length} of {activeSheet.rowCount} rows
          </span>
        </div>

        <div className={styles.headerActions}>
          {/* Re-arrange mode banner + Save/Cancel */}
          {isRearrangeMode && (
            <>
              <span className={styles.rearrangeBadge}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Re-arrange Mode
              </span>
              <button
                className={styles.saveButton}
                onClick={handleSaveRearrange}
                disabled={savingRearrange}
              >
                {savingRearrange ? (
                  <><div className={styles.smallSpinner}></div>Saving...</>
                ) : (
                  <><svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="2" />
                    <path d="M17 21v-8H7v8M7 3v5h8" stroke="currentColor" strokeWidth="2" />
                  </svg>Save Order</>
                )}
              </button>
              <button
                className={styles.cancelRearrangeButton}
                onClick={handleCancelRearrange}
                disabled={savingRearrange}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                Cancel
              </button>
            </>
          )}

          {/* Normal mode controls — hidden while rearranging */}
          {!isRearrangeMode && (
            <>
              {hasChanges && (
                <span className={styles.unsavedBadge}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" fill="currentColor" />
                  </svg>
                  Unsaved Changes
                </span>
              )}

              <button className={styles.rearrangeButton} onClick={handleEnterRearrangeMode}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M9 22V12h6v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Re-arrange Rows
              </button>

              <button className={styles.submitButton} onClick={handleGenerateDefine}>
                Regenerate Define
              </button>

              <button onClick={handleSave} className={styles.saveButton} disabled={!hasChanges || saving}>
                {saving ? (
                  <><div className={styles.smallSpinner}></div>Saving...</>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="2" />
                      <path d="M17 21v-8H7v8M7 3v5h8" stroke="currentColor" strokeWidth="2" />
                    </svg>
                    Save
                  </>
                )}
              </button>

              <button onClick={handleClose} className={styles.closeButton}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Sheet Tabs */}
      <div className={styles.sheetTabs}>
        {excelData.sheets.map((sheet, index) => (
          <button
            key={index}
            className={`${styles.sheetTab} ${activeSheetIndex === index ? styles.active : ''}`}
            onClick={() => {
              if (isRearrangeMode) return; // prevent tab switch during rearrange
              setActiveSheetIndex(index);
              clearFilters();
              resetChanges();
            }}
            disabled={isRearrangeMode}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
              <path d="M3 9h18M9 3v18" stroke="currentColor" strokeWidth="2" />
            </svg>
            {sheet.sheetName}
            <span className={styles.rowCount}>{sheet.rowCount}</span>
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <span className={styles.sheetInfo}>
            Sheet: <strong>{activeSheet.sheetName}</strong>
          </span>

          {/* Hide search/sort/filter in rearrange mode since data order must stay stable */}
          {!isRearrangeMode && (
            <>
              <div className={styles.searchBox}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className={styles.searchIcon}>
                  <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
                  <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder="Search across all columns..."
                  value={globalSearch}
                  onChange={(e) => setGlobalSearch(e.target.value)}
                />
                {globalSearch && (
                  <button className={styles.clearSearchButton} onClick={() => setGlobalSearch('')}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  </button>
                )}
              </div>

              {sortConfig && (
                <span className={styles.sortInfo}>
                  Sorted by: <strong>{sortConfig.column}</strong>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    {sortConfig.direction === 'asc' ? (
                      <path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    ) : (
                      <path d="M12 5v14M19 12l-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    )}
                  </svg>
                </span>
              )}

              {hasActiveFilters && (
                <button onClick={clearFilters} className={styles.clearAllButton}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  Clear All Filters
                </button>
              )}
            </>
          )}

          {isRearrangeMode && (
            <span className={styles.rearrangeHint}>
              Drag rows to reorder · click checkbox to multi-select then use "Move To..."
            </span>
          )}
        </div>

        <div className={styles.toolbarRight}>
          {isRearrangeMode && selectedRows.size > 0 && (
            <>
              <span className={styles.selectedInfo}>
                {selectedRows.size} row{selectedRows.size > 1 ? 's' : ''} selected
              </span>
              <button
                onClick={() => {
                  setRowsToMove(Array.from(selectedRows).sort((a, b) => a - b));
                  setShowMoveModal(true);
                }}
                className={styles.moveSelectedButton}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Move To...
              </button>
            </>
          )}

          {!isRearrangeMode && (
            <button onClick={handleAddRow} className={styles.addRowButton}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Add Row
            </button>
          )}
        </div>
      </div>

      {/* Excel Table */}
      <div className={styles.tableWrapper} ref={tableRef}>
        <table className={`${styles.excelTable} ${isRearrangeMode ? styles.rearrangeTable : ''}`}>
          <thead>
            <tr>
              <th className={styles.checkboxHeader}>
                <input
                  type="checkbox"
                  checked={selectedRows.size === filteredAndSortedData.length && filteredAndSortedData.length > 0}
                  onChange={handleSelectAll}
                  title="Select all rows"
                  disabled={!isRearrangeMode}
                />
              </th>
              <th className={styles.dragHandleHeader}></th>
              <th className={styles.rowNumberHeader}>#</th>
              {visibleColumns.map((column) => (
                <th
                  key={column}
                  className={styles.columnHeader}
                  style={{
                    width: columnWidths[column] || 200,
                    minWidth: columnWidths[column] || 200,
                    maxWidth: columnWidths[column] || 200,
                  }}
                >
                  <div className={styles.headerContent}>
                    <span className={styles.headerText} title={column}>{column}</span>
                    {/* Hide sort/filter controls in rearrange mode */}
                    {!isRearrangeMode && (
                      <div className={styles.headerActions}>
                        <button
                          className={`${styles.sortButton} ${sortConfig?.column === column ? styles.active : ''}`}
                          onClick={() => handleSort(column)}
                          title="Sort column"
                        >
                          {sortConfig?.column === column ? (
                            sortConfig.direction === 'asc' ? (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            ) : (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M12 5v14M19 12l-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 640 640">
                              <path fill="#ffffff" d="M470.6 566.6L566.6 470.6C575.8 461.4 578.5 447.7 573.5 435.7C568.5 423.7 556.9 416 544 416L480 416L480 96C480 78.3 465.7 64 448 64C430.3 64 416 78.3 416 96L416 416L352 416C339.1 416 327.4 423.8 322.4 435.8C317.4 447.8 320.2 461.5 329.3 470.7L425.3 566.7C437.8 579.2 458.1 579.2 470.6 566.7zM214.6 73.4C202.1 60.9 181.8 60.9 169.3 73.4L73.3 169.4C64.1 178.6 61.4 192.3 66.4 204.3C71.4 216.3 83.1 224 96 224L160 224L160 544C160 561.7 174.3 576 192 576C209.7 576 224 561.7 224 544L224 224L288 224C300.9 224 312.6 216.2 317.6 204.2C322.6 192.2 319.8 178.5 310.7 169.3L214.7 73.3z" />
                            </svg>
                          )}
                        </button>
                        <button
                          className={`${styles.filterButton} ${filters[column]?.length > 0 ? styles.active : ''}`}
                          onClick={() => setShowFilterDropdown(showFilterDropdown === column ? null : column)}
                          title="Filter column"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          {filters[column]?.length > 0 && (
                            <span className={styles.filterCount}>{filters[column].length}</span>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                  <div
                    className={styles.resizeHandle}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleColumnResize(column, e.clientX);
                    }}
                    title="Drag to resize column"
                  />
                  {/* Filter Dropdown */}
                  {!isRearrangeMode && showFilterDropdown === column && (
                    <div className={styles.filterDropdown} ref={filterDropdownRef}>
                      <div className={styles.filterDropdownHeader}>
                        <span>Filter by {column}</span>
                        <button
                          className={styles.closeDropdownButton}
                          onClick={() => { setShowFilterDropdown(null); setFilterSearch(''); }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" />
                          </svg>
                        </button>
                      </div>
                      <div className={styles.filterSearchBox}>
                        <input
                          type="text"
                          placeholder="Search options..."
                          value={filterSearch}
                          onChange={(e) => setFilterSearch(e.target.value)}
                          className={styles.filterSearchInput}
                        />
                      </div>
                      <div className={styles.filterOptions}>
                        {getFilteredOptions(column).map((value) => (
                          <label key={value} className={styles.filterOption}>
                            <input
                              type="checkbox"
                              checked={filters[column]?.includes(value) || false}
                              onChange={() => handleFilterToggle(column, value)}
                            />
                            <span>{value}</span>
                          </label>
                        ))}
                        {getFilteredOptions(column).length === 0 && (
                          <div className={styles.noOptions}>No options found</div>
                        )}
                      </div>
                    </div>
                  )}
                </th>
              ))}
              <th className={styles.actionsHeader}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedData.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length + 4} className={styles.emptyState}>
                  {globalSearch || Object.keys(filters).length > 0
                    ? 'No records match your filters'
                    : 'No data available'}
                </td>
              </tr>
            ) : (
              filteredAndSortedData.map((record, rowIndex) => (
                <tr
                  key={record._id || rowIndex}
                  className={`
                    ${styles.dataRow}
                    ${selectedRows.has(rowIndex) ? styles.selectedRow : ''}
                    ${dragOverIndex === rowIndex ? styles.dragOverRow : ''}
                    ${isRearrangeMode ? styles.rearrangeRow : ''}
                  `}
                  draggable={isRearrangeMode}
                  onDragStart={(e) => handleDragStart(e, rowIndex)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, rowIndex)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, rowIndex)}
                >
                  <td className={styles.checkboxCell}>
                    <input
                      type="checkbox"
                      checked={selectedRows.has(rowIndex)}
                      onChange={() => handleRowSelect(rowIndex)}
                      onClick={(e) => e.stopPropagation()}
                      disabled={!isRearrangeMode}
                    />
                  </td>
                  <td className={styles.dragHandleCell}>
                    <div className={`${styles.dragHandle} ${!isRearrangeMode ? styles.dragHandleDisabled : ''}`}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <circle cx="9" cy="5" r="1" fill="currentColor" />
                        <circle cx="9" cy="12" r="1" fill="currentColor" />
                        <circle cx="9" cy="19" r="1" fill="currentColor" />
                        <circle cx="15" cy="5" r="1" fill="currentColor" />
                        <circle cx="15" cy="12" r="1" fill="currentColor" />
                        <circle cx="15" cy="19" r="1" fill="currentColor" />
                      </svg>
                    </div>
                  </td>
                  <td className={styles.rowNumber}>{rowIndex + 1}</td>
                  {visibleColumns.map((column) => {
                    const isEditing = !isRearrangeMode && editingCell?.rowIndex === rowIndex && editingCell?.colKey === column;
                    const cellValue = record[column];

                    return (
                      <td
                        key={column}
                        className={`${styles.dataCell} ${isRearrangeMode ? styles.dataCellReadOnly : ''}`}
                        style={{ width: columnWidths[column] || 150 }}
                        onClick={() => handleCellClick(rowIndex, column)}
                      >
                        {isEditing ? (
                          <input
                            type="text"
                            className={styles.cellInput}
                            value={cellValue ?? ''}
                            onChange={(e) => handleCellEdit(rowIndex, column, e.target.value)}
                            onBlur={handleCellBlur}
                            onKeyDown={(e) => handleKeyDown(e, rowIndex, column)}
                            autoFocus
                          />
                        ) : (
                          <span
                            className={styles.cellContent}
                            title={cellValue !== null && cellValue !== undefined ? String(cellValue) : ''}
                          >
                            {cellValue !== null && cellValue !== undefined ? String(cellValue) : ''}
                          </span>
                        )}
                      </td>
                    );
                  })}
                  <td className={styles.actionsCell}>
                    {/* Hide delete button in rearrange mode */}
                    {!isRearrangeMode && (
                      <button
                        onClick={() => handleDeleteRow(rowIndex)}
                        className={styles.deleteButton}
                        title="Delete Row"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Drop Zone — only visible in rearrange mode while dragging */}
        {isRearrangeMode && isDragging && draggedRows.length > 0 && (
          <div
            ref={dropZoneRef}
            className={`${styles.dropZone} ${isDropZoneHovered ? styles.dropZoneHovered : ''}`}
            onClick={handleDropZoneClick}
            onDragOver={handleDropZoneDragOver}
            onDragLeave={handleDropZoneDragLeave}
            onDrop={handleDropZoneDrop}
          >
            <div className={styles.dropZoneContent}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div className={styles.dropZoneText}>
                <strong>Move to specific row</strong>
                <span>Drop here or click to enter row number</span>
                <span className={styles.dropZoneHint}>Moving {draggedRows.length} row{draggedRows.length > 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Drag Preview */}
      {isDragging && dragPreviewPosition && (
        <div
          className={styles.dragPreview}
          style={{ left: dragPreviewPosition.x + 15, top: dragPreviewPosition.y + 15 }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="9" cy="5" r="1" fill="currentColor" />
            <circle cx="9" cy="12" r="1" fill="currentColor" />
            <circle cx="9" cy="19" r="1" fill="currentColor" />
            <circle cx="15" cy="5" r="1" fill="currentColor" />
            <circle cx="15" cy="12" r="1" fill="currentColor" />
            <circle cx="15" cy="19" r="1" fill="currentColor" />
          </svg>
          <span>{draggedRows.length} row{draggedRows.length > 1 ? 's' : ''}</span>
        </div>
      )}

      {/* Move to Row Modal */}
      {showMoveModal && (
        <div className={styles.modalOverlay} onClick={handleCancelMove}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Move to Row Position</span>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.modalDescription}>
                Moving {rowsToMove.length} row{rowsToMove.length > 1 ? 's' : ''} to a new position
              </p>
              <label className={styles.inputLabel}>
                Row Number (1 - {filteredAndSortedData.length})
              </label>
              <input
                type="number"
                className={styles.rowNumberInput}
                placeholder={`Enter row number (1-${filteredAndSortedData.length})`}
                value={targetRowNumber}
                onChange={(e) => setTargetRowNumber(e.target.value)}
                min="1"
                max={filteredAndSortedData.length}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleMoveToRow();
                  else if (e.key === 'Escape') handleCancelMove();
                }}
              />
            </div>
            <div className={styles.modalFooter}>
              <button onClick={handleCancelMove} className={styles.cancelButton}>Cancel</button>
              <button onClick={handleMoveToRow} className={styles.moveButton}>Move Rows</button>
            </div>
          </div>
        </div>
      )}

      {showUnsavedChangesModal && (
        <Modal isOpen={showUnsavedChangesModal} onClose={handleCancelGenerateDefine} title="Unsaved Changes">
          <div className={styles.deleteModalContent}>
            <p>There are unsaved changes in the current file. Do you really want to generate define?</p>
            <div className={styles.deleteModalActions}>
              <button className={styles.deleteModalButton} onClick={handleConfirmGenerateDefine}>Yes</button>
              <button className={styles.cancelModalButton} onClick={handleCancelGenerateDefine}>Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {showDeleteModal && (
        <Modal isOpen={showDeleteModal} onClose={handleCancelDelete} title="Confirm Delete">
          <div className={styles.deleteModalContent}>
            <p>Are you sure you want to delete this row?</p>
            <div className={styles.deleteModalActions}>
              <button className={styles.deleteModalButton} onClick={handleConfirmDelete}>Delete</button>
              <button className={styles.cancelModalButton} onClick={handleCancelDelete}>Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {showCloseModal && (
        <Modal isOpen={showCloseModal} onClose={handleCancelClose} title="Unsaved Changes">
          <div className={styles.deleteModalContent}>
            <p>You have unsaved changes. Are you sure you want to close?</p>
            <div className={styles.deleteModalActions}>
              <button className={styles.deleteModalButton} onClick={handleConfirmClose}>Close</button>
              <button className={styles.cancelModalButton} onClick={handleCancelClose}>Cancel</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ExcelViewer;